/**
 * Uebersicht nach dem Login: Begruessung und die Auswahl der Ereignisse fuer „Neu seit gestern“.
 * Reine Funktionen, damit sie ohne Browser testbar sind.
 */
import type { AuditEntry } from './api.js';
import { auditLabel } from './audit-labels.js';
import { serviceInfo } from './system-services.js';

/** Ereignisse, die fuer Menschen interessant sind; Datenfluss (data.object.put, data.ingest, Laeufe) bleibt im Protokoll. */
const INTERESTING = new Set([
  'dataset.create',
  'dataset.create.bundle',
  'dataset.update',
  'dataset.archive',
  'dataset.delete',
  'source.create',
  'source.delete',
  'harvest.run',
  'harvest.source.create',
  'widget.create',
  'widget.update',
  'widget.delete',
  'derivation.create',
  'alert_rule.create',
  'publisher.create',
  'data_service.create',
  'map_service.create',
  'tenant.update',
  'system.restart_requested',
  'link_tile.create',
]);

export interface RecentEvent {
  id: string;
  label: string;
  /** Name des Datensatzes o. Ae., wenn das Ereignis einen mitbringt. */
  detail: string | null;
  at: string;
}

function detailOf(e: AuditEntry): string | null {
  const service = e.execution_result?.service;
  if (e.action === 'system.restart_requested' && typeof service === 'string') return serviceInfo(service)?.label ?? service;
  for (const src of [e.execution_result, e.catalog_context, e.validation_result]) {
    if (!src) continue;
    for (const k of ['title', 'name', 'service', 'slug']) {
      const v = src[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return null;
}

/** Objekte, die im Zeitraum angelegt und wieder geloescht wurden: unterm Strich nichts Neues (z. B. Testlaeufe). */
function kamUndGing(entries: AuditEntry[], since: Date): Set<string> {
  const angelegt = new Set<string>();
  const geloescht = new Set<string>();
  for (const e of entries) {
    if (!e.asset_id || new Date(e.occurred_at) < since) continue;
    const art = e.action.split('.').slice(1);
    if (art.includes('create')) angelegt.add(e.asset_id);
    if (art.includes('delete')) geloescht.add(e.asset_id);
  }
  return new Set([...angelegt].filter((id) => geloescht.has(id)));
}

/**
 * Die juengsten lesbaren Ereignisse seit `since`. Gleiche Ereignisse am selben Objekt direkt
 * hintereinander (z. B. fuenfmal „Datensatz geaendert“) erscheinen einmal; was im Zeitraum
 * angelegt und wieder geloescht wurde, erscheint gar nicht.
 */
export function recentEvents(entries: AuditEntry[], since: Date, max = 6): RecentEvent[] {
  const out: RecentEvent[] = [];
  const weg = kamUndGing(entries, since);
  let lastKey = '';
  for (const e of entries) {
    if (new Date(e.occurred_at) < since) continue;
    if (!INTERESTING.has(e.action)) continue;
    if (e.asset_id && weg.has(e.asset_id)) continue;
    const label = auditLabel(e.action);
    const key = `${e.action}|${e.asset_id ?? ''}`;
    if (key === lastKey) continue;
    lastKey = key;
    out.push({ id: e.id, label, detail: detailOf(e), at: e.occurred_at });
    if (out.length >= max) break;
  }
  return out;
}

/** „Guten Morgen“ bis 11 Uhr, „Guten Abend“ ab 18 Uhr, sonst „Guten Tag“. */
export function greeting(now: Date): string {
  const h = now.getHours();
  if (h < 11) return 'Guten Morgen';
  if (h >= 18) return 'Guten Abend';
  return 'Guten Tag';
}

/** Vorname aus dem Token, sonst der Teil vor dem @ des Benutzernamens. */
export function displayFirstName(token: { given_name?: unknown; preferred_username?: unknown } | undefined): string | null {
  const given = typeof token?.given_name === 'string' ? token.given_name.trim() : '';
  if (given) return given;
  const user = typeof token?.preferred_username === 'string' ? token.preferred_username.trim() : '';
  if (!user) return null;
  return user.split('@')[0] || null;
}

/** „gerade eben“, „vor 5 Min.“, „vor 3 Std.“, „gestern“, „vor 4 Tagen“, sonst das Datum. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const t = new Date(iso);
  const min = Math.round((now.getTime() - t.getTime()) / 60_000);
  if (!Number.isFinite(min)) return '';
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min.`;
  const std = Math.round(min / 60);
  if (std < 24 && t.getDate() === now.getDate()) return `vor ${std} Std.`;
  const tage = Math.round((new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime()) / 86_400_000);
  if (tage <= 1) return 'gestern';
  if (tage < 7) return `vor ${tage} Tagen`;
  return t.toLocaleDateString('de-DE');
}
